/**
 * Setting values in the words the editors use for them, keyed by the setting's dot path.
 *
 * One formatter for every read-only view of a setting: the deploy Overview's "All settings", the
 * changes an update makes to an existing deployment, and the deployment page. A stored token
 * ("suppress", "appIcon", `false`) is shown as the option the admin picked in the editor.
 *
 * Paths come in three shapes, all handled here: the saved template configuration
 * (`identity.dialogStyle`), the user experience settings as they travel on the install command
 * (`welcome.deferTimes`, `closeAppsMode`), and Graph's win32LobApp properties
 * (`installExperience.deviceRestartBehavior`).
 */
import { plural } from './plural'
import { TEMPLATE_SETTING_LABELS } from './templateSettingLabels'

/** Intune's own options for "Device restart behavior", as the Advanced settings editor lists them. */
export const RESTART_BEHAVIOUR_OPTIONS: { value: string; label: string }[] = [
  { value: 'basedOnReturnCode', label: 'Determine behaviour based on return codes' },
  { value: 'suppress', label: 'No specific action (suppress restart)' },
  { value: 'allow', label: 'App install may force a device restart' },
  { value: 'force', label: 'Intune will force a mandatory device restart' },
]

const RESTART: Record<string, string> = {
  ...Object.fromEntries(RESTART_BEHAVIOUR_OPTIONS.map((o) => [o.value, o.label])),
  allow: 'App install may force a device restart',
}
const INSTALL_CONTEXT = { system: 'System', user: 'User' }
const INSTALL_MODE = {
  notify: 'Ask the user to close it',
  autoClose: 'Close it automatically',
  skip: 'Skip the install',
  perform: 'Install anyway',
}
const UNINSTALL_MODE = {
  notify: 'Ask the user to close it',
  autoClose: 'Close it automatically',
  skip: 'Skip the uninstall',
  perform: 'Uninstall anyway',
}
const INSTALL_TIMEOUT = { defer: 'Postpone for them', proceed: 'Install anyway' }
const UNINSTALL_TIMEOUT = { defer: 'Postpone for them', proceed: 'Uninstall anyway' }
const DIALOG_STYLE = { modern: 'Fluent (modern)', classic: 'Classic', Fluent: 'Fluent (modern)', Classic: 'Classic' }
const LANGUAGE = { null: 'Auto-detect' }

/** Stored value -> the option's label. Keys are `String(value)`, so `false` and `null` work too. */
const VALUE_LABELS: Record<string, Record<string, string>> = {
  restartBehaviour: RESTART,
  deviceRestartBehavior: RESTART,
  'installExperience.deviceRestartBehavior': RESTART,
  installContext: INSTALL_CONTEXT,
  runAsAccount: INSTALL_CONTEXT,
  'installExperience.runAsAccount': INSTALL_CONTEXT,
  'updateOnly.matchUnmanaged': { false: 'Installs from Agentic Packr', true: 'Any install of this app' },
  'automatedUpdates.uninstallPrevious': {
    false: 'Let the installer update it',
    true: 'Uninstall it first',
  },
  'identity.dialogStyle': DIALOG_STYLE,
  dialogStyle: DIALOG_STYLE,
  'identity.logoSource': { appIcon: "App's own icon (automatic)", custom: 'Custom logo' },
  'identity.languageOverride': LANGUAGE,
  language: LANGUAGE,
  'closeApps.mode': INSTALL_MODE,
  closeAppsMode: INSTALL_MODE,
  'uninstall.mode': UNINSTALL_MODE,
  'defer.onTimeout': INSTALL_TIMEOUT,
  onTimeout: INSTALL_TIMEOUT,
  'uninstall.onTimeout': UNINSTALL_TIMEOUT,
  'welcome.countdownMode': { Always: 'Shown right away', AfterDefers: 'After postpones run out' },
  'uninstall.countdownMode': { Always: 'Shown right away', AfterDefers: 'After postpones run out' },
  'welcome.promptCondition': { always: 'Always', ifRunning: 'Only when the app is running' },
  config_transport: {
    b64: 'In the install command',
    inline: 'In the install command',
    script: 'In a PowerShell script attached to the app',
    url: 'Downloaded from cloud storage at install time',
  },
}

/** A countdown length as the user experience editor offers it: "5 minutes", "48 seconds". */
export function seconds(n: number): string {
  return n % 60 === 0 && n > 0 ? plural(n / 60, 'minute') : plural(n, 'second')
}

/** Numbers that carry a unit. */
const NUMBER_FORMATS: Record<string, (n: number) => string> = {
  'closeApps.countdownSeconds': seconds,
  'uninstall.countdownSeconds': seconds,
  'welcome.countdownSeconds': seconds,
  'welcome.closeProcessesCountdown': seconds,
  defaultTimeout: seconds,
  'uninstall.defaultTimeout': seconds,
  'timers.notifyTimeoutMinutes': (n) => plural(n, 'minute'),
  'uninstall.notifyTimeoutMinutes': (n) => plural(n, 'minute'),
  'defer.count': (n) => plural(n, 'time'),
  'uninstall.deferCount': (n) => plural(n, 'time'),
  'welcome.deferTimes': (n) => plural(n, 'time'),
  'uninstall.deferTimes': (n) => plural(n, 'time'),
  'automatedUpdates.soakDays': (n) => (n === 0 ? 'None' : plural(n, 'day')),
  'automatedUpdates.keepVersions': (n) => (n === 0 ? 'Only the latest' : `${n} previous`),
  'requirements.diskSpaceMb': (n) => `${n} MB`,
  'requirements.memoryMb': (n) => `${n} MB`,
  'requirements.cpuSpeedMhz': (n) => `${n} MHz`,
  minimumFreeDiskSpaceMB: (n) => `${n} MB`,
  minimumFreeDiskSpaceInMB: (n) => `${n} MB`,
  minimumMemoryMB: (n) => `${n} MB`,
  minimumMemoryInMB: (n) => `${n} MB`,
  minimumCpuSpeedMHz: (n) => `${n} MHz`,
  minimumCpuSpeedInMHz: (n) => `${n} MHz`,
}

/**
 * The user experience settings as they travel on the install command, named as the user
 * experience editor names the same control.
 */
const RUNTIME_SETTING_LABELS: Record<string, string> = {
  interactive: 'Interactive',
  closeAppsMode: 'When the app is already running',
  onTimeout: 'If they do nothing',
  defaultTimeout: 'Wait for a response',
  companyName: 'Company name',
  dialogStyle: 'Dialog style',
  fluentAccent: 'Accent colour',
  language: 'Dialog language',
  balloonNotifications: 'Show balloon notifications',
  'welcome.allowDefer': 'Let the user postpone the install',
  'welcome.deferTimes': 'How many times',
  'welcome.persistPrompt': 'Make them respond',
  'welcome.countdownSeconds': 'Countdown before closing',
  'welcome.closeProcessesCountdown': 'Countdown before closing',
  'welcome.countdownMode': 'Show the countdown',
  'welcome.promptCondition': 'When to show the dialog',
  'welcome.deferMode': 'Postpone limit',
  'progress.enabled': 'Show install progress',
  'progress.statusMessage': 'Progress message',
  'uninstall.allowDefer': 'Let the user postpone the uninstall',
  'uninstall.deferTimes': 'Uninstall › How many times',
  'uninstall.countdownMode': 'Uninstall › Show the countdown',
  'uninstall.defaultTimeout': 'Uninstall › Wait for a response',
  'branding.productName': 'Product name',
}

/** Every setting label the read-only views use, before a view's own overrides. */
export const SETTING_LABELS: Record<string, string> = {
  ...RUNTIME_SETTING_LABELS,
  ...TEMPLATE_SETTING_LABELS,
}

/** Section headings for the first path segment, as the editors title their steps. */
export const SETTING_GROUP_LABELS: Record<string, string> = {
  intuneMetadata: 'App information',
  naming: 'Names',
  requirements: 'Requirements',
  updateOnly: 'Update-only app',
  automatedUpdates: 'Automated updates',
  identity: 'Appearance',
  closeApps: 'Install experience',
  defer: 'Postponing',
  timers: 'Timing',
  dialogs: 'Dialogs',
  postInstall: 'After install',
  uninstall: 'Uninstall experience',
  welcome: 'Install experience',
  progress: 'Progress',
  schedule: 'Schedule',
  branding: 'Branding',
  installExperience: 'Program',
}

/** The last segment, for settings whose table entry is keyed by the leaf alone. */
const leafOf = (path: string) => path.slice(path.lastIndexOf('.') + 1)

function lookup<T>(table: Record<string, T>, path: string): T | undefined {
  return table[path] ?? (path.includes('.') ? undefined : table[leafOf(path)])
}

/**
 * One setting's value as the editor shows it. `path` is the setting's dot path (or its bare name).
 */
export function formatSettingValue(path: string, value: unknown): string {
  const labels = lookup(VALUE_LABELS, path)
  if (labels) {
    const hit = labels[String(value ?? null)]
    if (hit) return hit
  }
  if (value === null || value === undefined || value === '') return '—'
  if (typeof value === 'boolean') return value ? 'On' : 'Off'
  if (typeof value === 'number') {
    const unit = lookup(NUMBER_FORMATS, path)
    return unit ? unit(value) : String(value)
  }
  if (Array.isArray(value)) {
    if (!value.length) return 'None'
    // A list of plain values (categories, architectures) reads as the list itself.
    if (value.every((v) => typeof v !== 'object' || v === null)) {
      return value.map((v) => (labels?.[String(v)] ?? String(v))).join(', ')
    }
    return plural(value.length, 'entry', 'entries')
  }
  if (typeof value === 'object') return plural(Object.keys(value).length, 'setting')
  return String(value)
}

/** Graph's comma-separated architecture list ("x64,arm64") as the editor's list ("x64, arm64"). */
export function formatArchitectures(value: string | null | undefined): string {
  if (!value) return 'All'
  return value
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean)
    .join(', ')
}
